import { Avatar, Loading, SecondCustomInput, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import { QUERY_KEYS } from "@/libraries/constant";
import { isAuthenticating } from "@/libraries/helper";
import { LoginSsoPayloadSchema } from "@/schemas";
import type { ResError } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MailOutlined from "@mui/icons-material/MailOutlined";
import Button from "@mui/material/Button";
import { useMutation } from "@tanstack/react-query";
import {
	Link,
	createFileRoute,
	redirect,
	useRouter,
} from "@tanstack/react-router";
import omit from "lodash/omit";
import { useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";

export const Route = createFileRoute("/sso/signin")({
	beforeLoad: async () => {
		const isAuthed = isAuthenticating();
		if (isAuthed) {
			throw redirect({
				to: "/dashboard",
			});
		}
	},
	component: RouteComponent,
});

function RouteComponent() {
	const router = useRouter();
	const [passwordVisible, setPasswordVisible] = useState(false);
	const { mutateAsync, isError } = useMutation<
		unknown,
		ResError,
		LoginSsoPayloadSchema
	>({
		mutationKey: [QUERY_KEYS.MUTATE_AUTH_LOGIN_SSO],
	});
	const {
		control,
		setError,
		handleSubmit,
		formState: { isDirty, isValid, isSubmitting },
	} = useForm<LoginSsoPayloadSchema>({
		mode: "all",
		resolver: zodResolver(LoginSsoPayloadSchema),
		defaultValues: {
			username: "",
			password: "",
		},
	});

	const onSubmit: SubmitHandler<LoginSsoPayloadSchema> = async (data, e) => {
		e?.stopPropagation();
		e?.preventDefault();
		try {
			await mutateAsync(data);
			return router.invalidate();
		} catch (error) {
			setError("username", {
				type: "validate",
				message:
					"The email address or password is incorrect. Please try again.",
			});
			setError("password", {
				type: "validate",
				message:
					"The email address or password is incorrect. Please try again.",
			});
		}
	};

	return (
		<>
			<BackAppBar />
			<form
				onSubmit={handleSubmit(onSubmit)}
				className="container py-4 flex-1 flex flex-col items-center justify-normal gap-8"
			>
				{isError && (
					<div className="bg-danger-100 w-full flex items-start border border-danger-300 rounded-xl gap-2 p-4">
						<ErrorRoundedIcon className="text-danger-300 !text-base" />
						<div>
							<p className="text-xs !leading-4 text-dark">
								The email address or password is incorrect. Please try again.
							</p>
						</div>
					</div>
				)}
				<Link
					to="/sso/select-provider"
					className="w-full flex flex-row items-center justify-normal gap-4 px-[14px] border border-zinc-950 rounded-xl h-14"
				>
					<Avatar
						src="/images/mhnexus.png"
						title="MHNexus"
						className="[&_img]:object-center [&_img]:object-contain"
					/>
					<Typography fontWeight={400} fontSize={16} className="flex-1">
						MHNexus
					</Typography>
					<KeyboardArrowDownIcon />
				</Link>
				<Controller
					control={control}
					name="username"
					render={({ field, fieldState }) => (
						<SecondCustomInput
							label="Email"
							type="email"
							errorMessage={fieldState.error?.message}
							isShowErrorMessage={!isError}
							startIcon={<MailOutlined />}
							{...omit(field, ["ref"])}
						/>
					)}
				/>
				<Controller
					control={control}
					name="password"
					render={({ field, fieldState }) => (
						<SecondCustomInput
							type="password"
							label="Password"
							errorMessage={fieldState.error?.message}
							isShowErrorMessage={!isError}
							onToggleVisibility={() => setPasswordVisible(!passwordVisible)}
							showPassword={passwordVisible}
							startIcon={<LockOutlinedIcon />}
							{...omit(field, ["ref"])}
						/>
					)}
				/>
				<Button
					type="submit"
					variant="contained"
					color="primary"
					size="large"
					fullWidth
					className="!rounded-full h-14"
					disabled={!isDirty || !isValid || isSubmitting}
				>
					Sign In
				</Button>
			</form>
			<div className="w-full flex flex-wrap justify-center items-center text-tiny gap-1 mb-6">
				<p>HEMI Teams</p>
				<Link to="/term-service" preload={false}>
					<p className="font-semibold text-nowrap">Terms of Use,</p>
				</Link>
				<Link to="/privacy-policy" preload={false}>
					<p className="font-semibold text-nowrap">Privacy Policy,</p>
				</Link>
				<Link to="/cookie-policy" preload={false}>
					<p className="font-semibold text-nowrap">Cookie Policy</p>
				</Link>
			</div>
			<Loading
				status={isSubmitting}
				title="Verification in Progress"
				message="This can take view moments to complete, please wait..."
			/>
		</>
	);
}
